import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Chip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  People,
  SportsTennis,
  Business,
  EmojiEvents,
  Score,
  Settings,
  Logout,
  AccountCircle,
  CalendarToday,
  OpenInNew,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { getConfig } from '../../config/environment';

const drawerWidth = 240;

interface AppLayoutProps {
  children: React.ReactNode;
}

  const menuItems = [
    { text: 'Matches', icon: <SportsTennis />, path: '/matches' },
    { text: 'Tournaments', icon: <EmojiEvents />, path: '/tournaments' },
    { text: 'Players', icon: <People />, path: '/users' },
    { text: 'Clubs', icon: <Business />, path: '/clubs' },
    { text: 'Statistics', icon: <Score />, path: '/scores' },
    { text: 'Settings', icon: <Settings />, path: '/settings' },
  ];

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [ssoError, setSsoError] = useState<string | null>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleProfileMenuClose();
  };

  const handleOpenPadelSkill = async () => {
    try {
      const response = await apiService.getSsoToken();
      const { sso_token } = response.data;
      const padelSkillUrl = `${getConfig().PADELSKILL_URL}/sso?token=${sso_token}`;
      window.open(padelSkillUrl, '_blank');
      handleProfileMenuClose();
    } catch (error) {
      setSsoError(t('sso.ssoError', 'Failed to generate SSO token'));
      handleProfileMenuClose();
    }
  };

  const drawer = (
    <div>
      <Toolbar>
        <Box display="flex" alignItems="center" gap={1}>
          <img 
            src="/logo-no-text.png" 
            alt="PadPal Logo" 
            style={{ 
              width: 64, 
              height: 64, 
              filter: 'brightness(0) invert(1)',
              marginRight: 8 
            }} 
          />
          <Typography variant="h6" noWrap component="div">
            PadPal
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
        {user?.is_admin && (
          <>
            <Divider />
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname.startsWith('/admin') || location.pathname === '/dashboard'}
                onClick={() => {
                  navigate('/dashboard');
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon>
                  <AccountCircle />
                </ListItemIcon>
                <ListItemText primary="Admin Panel" />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
            <img 
              src="/logo-no-text.png" 
              alt="PadPal Logo" 
              style={{ 
                width: 64, 
                height: 64, 
                filter: 'brightness(0) invert(1)',
                marginRight: 12 
              }} 
            />
            <Typography variant="h6" noWrap component="div">
              PadPal
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            {user?.is_admin && (
              <Chip
                label="Admin"
                color="primary"
                size="medium"
                icon={<AccountCircle />}
              />
            )}
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
        }}
      >
        {children}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
      >
        <MenuItem onClick={() => navigate('/settings')}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={handleOpenPadelSkill}>
          <ListItemIcon>
            <OpenInNew fontSize="small" />
          </ListItemIcon>
          {t('sso.openPadelSkill', 'Open PadelSkill')}
        </MenuItem>
        {user?.is_admin && (
          <MenuItem onClick={() => navigate('/admin/dashboard')}>
            <ListItemIcon>
              <AccountCircle fontSize="small" />
            </ListItemIcon>
            Admin Panel
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
      <Snackbar
        open={!!ssoError}
        autoHideDuration={6000}
        onClose={() => setSsoError(null)}
      >
        <Alert onClose={() => setSsoError(null)} severity="error">
          {ssoError}
        </Alert>
      </Snackbar>
    </Box>
  );
}; 